"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, Activity, Scale, Wrench, ShoppingBag, Building2, ArrowRight, CircleCheck as CheckCircle2, Briefcase } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsList = [{
  title: "Accountants & Financial Services",
  description: "Targeting high-value retainer clients through targeted local and national SEO campaigns structured for competitive financial search intent.",
  icon: Calculator,
  tag: "Finance & B2B"
}, {
  title: "Dentists & Private Healthcare",
  description: "Driving high-converting patient inquiries for treatments like dental implants, Invisalign, and private cosmetic procedures.",
  icon: Activity,
  tag: "Healthcare"
}, {
  title: "Solicitors & Legal Practice",
  description: "Capturing high-intent searches across family law, corporate practice, personal injury, and conveyancing with compliant SEO.",
  icon: Scale,
  tag: "Legal Services"
}, {
  title: "Plumbers, Electricians & Trades",
  description: "Helping trade professionals dominate local map packs and generate urgent, phone-call-ready local business leads.",
  icon: Wrench,
  tag: "Local Trades"
}, {
  title: "E-commerce & Direct-to-Consumer",
  description: "Optimising technical store architecture, faceted category navigation, and high-margin product pages for sustained revenue growth.",
  icon: ShoppingBag,
  tag: "E-commerce"
}, {
  title: "Property & Real Estate",
  description: "Positioning estate agencies, surveyors, and property developers at the top of organic search for local buyers and sellers.",
  icon: Building2,
  tag: "Real Estate"
}];
const highlights = ["Direct consultant access", "Tailored industry keyword research", "Transparent monthly reporting", "No junior account managers"];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="relative bg-background py-20 text-foreground md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <Briefcase className="size-3.5 text-primary" />
            <span>Sectors Served</span>
          </Badge>

          <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Bespoke SEO Strategies Built for Your Specific Industry
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {"I don't use generic SEO templates. Every industry has distinct search intent, compliance considerations, and conversion pathways. You work directly with me to build a strategy engineered for your niche."}
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sectorsList.map((sector, i) => {
          const IconComponent = sector.icon;
          return <div data-index={i} key={sector.title} className="h-full">
                <motion.div variants={itemVariants} className="h-full">
                  <Card className="group relative flex h-full flex-col justify-between overflow-hidden border-border bg-card transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <IconComponent className="size-6 shrink-0" />
                        </div>
                        <Badge variant="outline" className="border-border bg-background text-xs text-muted-foreground">
                          {sector.tag}
                        </Badge>
                      </div>

                      <CardTitle className="mt-5 font-serif text-xl font-semibold text-card-foreground group-hover:text-primary">
                        {sector.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-1 flex-col justify-between pt-0">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {sector.description}
                      </CardDescription>

                      <div className="mt-6 pt-4">
                        <Link to="/ContactPage" className="inline-flex items-center text-xs font-semibold text-primary transition-colors hover:text-primary/80">
                          <span>Discuss your sector SEO</span>
                          <ArrowRight className="ml-1 size-3.5 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>;
        })}
        </motion.div>

        <div className="mt-16 rounded-2xl border border-border bg-muted/50 p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
            <div className="max-w-2xl text-center lg:text-left">
              <h3 className="font-serif text-xl font-bold text-foreground sm:text-2xl">
                {"Don't see your specific market listed?"}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                {"Whether you run a specialised B2B consultancy, local service business, or niche e-commerce store, I apply deep search intent analysis to craft an organic growth strategy suited to your goals."}
              </p>

              <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 lg:justify-start">
                {highlights.map((item, idx) => <div key={idx} className="flex items-center gap-1.5 text-xs font-medium text-foreground sm:text-sm">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="font-medium shadow-md">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border bg-background">
                <Link to="/CaseStudiesPage">
                  View Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}